import React, { useMemo, useState } from "react";

import { useTimezone } from "@contexts/useTimezone";
import { QontyLoading } from "@components/QontyLoading";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { Application } from "@core/Application";
import { Organization } from "@models/Organization";
import { SecondFactorAuthenticationPolicyMode, SecondFactorAuthenticationService } from "@services/SecondFactorAuthenticationService";
import { UsersService } from "@services/UsersService";
import { HollowedButton, IconButton, NormalButton, RefreshButton } from "@shared/designSystem/Button";
import { ComboBox, ComboBoxOption } from "@shared/designSystem/ComboBox";
import {
    ColumnsContainer,
    HorizontalContainer,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import { DetailField } from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import SearchBar from "@shared/designSystem/SearchBar";
import { TabScreenSleek } from "@shared/designSystem/TabScreen";
import FilterPillGroup, { FilterPillOption } from "@shared/designSystem/FilterPillGroup";
import { SmallText, Text } from "@shared/designSystem/Texts";
import { Flap } from "@shared/models/Flap";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { QSwitch } from "@shared/designSystem/QSwitch";
import {
    DeviceStatusFilter,
    TwoFactorAuthenticationDeviceRow,
    TwoFactorStatusFilter,
    useTwoFactorAuthenticationDevicesViewModel,
    useTwoFactorAuthenticationModalViewModel,
} from "./TwoFactorAuthenticationModalViewModel";

interface TwoFactorAuthenticationModalScreenProps {
    isOpen: boolean;
    onClose: () => void;
    org: Organization;
}

const tableGridTemplate = "minmax(0, 1.7fr) minmax(100px, 0.8fr) minmax(0, 1fr) minmax(0, 1.35fr) minmax(72px, 72px) minmax(96px, 96px)";
const devicesTableGridTemplate = "minmax(0, 1.4fr) minmax(0, 1.5fr) minmax(96px, 0.7fr) minmax(84px, 0.6fr) minmax(90px, 0.7fr) minmax(140px, 1fr) minmax(104px, 104px)";

const modeOptions: Array<{
    mode: SecondFactorAuthenticationPolicyMode;
    title: string;
    description: string;
}> = [
    {
        mode: "all",
        title: "Todos",
        description: "2FA se aplica a todos los colaboradores activos. Desactiva excepciones manuales.",
    },
    {
        mode: "selective",
        title: "Seleccion manual",
        description: "2FA queda apagado por defecto. Activa solo los colaboradores elegidos.",
    },
];

const modeComboOptions: ComboBoxOption[] = modeOptions.map((option) => ({
    id: option.mode,
    title: option.title.localized(),
}));

const TwoFactorAuthenticationTableText: React.FC<{
    text?: string;
    isDimmed: boolean;
}> = ({ text, isDimmed }) => {
    const displayText = text?.trim() || "-";

    return (
        <div style={{ opacity: isDimmed ? 0.48 : 1, minWidth: 0 }}>
            <Text
                text={displayText}
                truncate={true}
                title={displayText}
            />
        </div>
    );
};

const TwoFactorAuthenticationModalScreen: React.FC<TwoFactorAuthenticationModalScreenProps> = ({
    isOpen,
    onClose,
    org,
}) => {
    const [selectedTab, setSelectedTab] = useState("users");
    const [deviceToDeactivate, setDeviceToDeactivate] = useState<TwoFactorAuthenticationDeviceRow | undefined>();
    const [expandedDeviceInstallationID, setExpandedDeviceInstallationID] = useState<string | undefined>();
    const [isDeactivationConfirmationSaving, setIsDeactivationConfirmationSaving] = useState(false);
    const { timezone } = useTimezone();
    const usersService = useMemo(() => (
        new UsersService(Application.shared().coreDependencies.httpClient)
    ), []);
    const secondFactorAuthenticationService = useMemo(() => (
        new SecondFactorAuthenticationService(Application.shared().coreDependencies.httpClient)
    ), []);

    const viewModel = useTwoFactorAuthenticationModalViewModel({
        organizationID: org.id,
        isOpen,
        usersService,
        secondFactorAuthenticationService,
        timezone,
    });
    const devicesViewModel = useTwoFactorAuthenticationDevicesViewModel({
        organizationID: org.id,
        isOpen,
        collaborators: viewModel.collaborators,
        secondFactorAuthenticationService,
        timezone,
    });

    const selectedModeOption = modeComboOptions.find((option) => option.id === viewModel.mode);
    const selectedModeDescription = modeOptions.find((option) => option.mode === viewModel.mode)?.description;

    if (!org.newFlags?.Presenteeism2FA) {
        return null;
    }

    const handleConfirmDeactivateDevice = async () => {
        if (!deviceToDeactivate) {
            return;
        }

        setIsDeactivationConfirmationSaving(true);
        try {
            await devicesViewModel.deactivateDevice(deviceToDeactivate.deviceInstallationID);
            ToastManager.shared().showSuccessMessage("Dispositivo desactivado.".localized());
            setDeviceToDeactivate(undefined);
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo desactivar el dispositivo.".localized());
        } finally {
            setIsDeactivationConfirmationSaving(false);
        }
    };

    const renderUsersBody = () => {
        if (viewModel.isLoading) {
            return (
                <QontyLoading
                    text={"Cargando configuracion 2FA...".localized()}
                    withQonty={true}
                    darkMode={false}
                />
            );
        }

        if (viewModel.errorMessage) {
            return (
                <VerticalContainer
                    width="100%"
                    height="100%"
                    alignment="center"
                    justifyContent="center"
                    gap={designSystemStyles.gapMd}
                >
                    <QontyEmptyState
                        text={viewModel.errorMessage}
                        darkMode={false}
                    />
                    <RefreshButton
                        onClick={viewModel.refresh}
                        loading={viewModel.isRefreshing}
                    />
                </VerticalContainer>
            );
        }

        if (viewModel.totalCount === 0) {
            return (
                <QontyEmptyState
                    text={"No hay colaboradores activos para configurar.".localized()}
                    darkMode={false}
                />
            );
        }

        if (viewModel.visibleRows.length === 0) {
            return (
                <QontyEmptyState
                    text={"No hay colaboradores que coincidan con la busqueda.".localized()}
                    darkMode={false}
                />
            );
        }

        return (
            <VerticalContainer
                width="100%"
                height="100%"
                gap={designSystemStyles.gapSm}
                minHeight="0"
            >
                <TableHeaderContainer
                    height={designSystemStyles.sizeRowMedium}
                    gridTemplateColumns={tableGridTemplate}
                    gap={designSystemStyles.gapSm}
                >
                    <TableHeaderCell text="Nombre" truncate={true} />
                    <TableHeaderCell text="Identificacion" truncate={true} />
                    <TableHeaderCell text="Empresa" truncate={true} />
                    <TableHeaderCell text="Celular" truncate={true} />
                    <TableHeaderCell text="2FA" justifyContent="center" />
                    <TableHeaderCell text={"App".localized()} justifyContent="center" />
                </TableHeaderContainer>
                <VerticalContainer
                    width="100%"
                    height="100%"
                    minHeight="0"
                    scrollable={true}
                    gap={designSystemStyles.gapSm}
                >
                    {viewModel.visibleRows.map((row) => (
                        <TableRowContainer
                            key={row.id}
                            minHeight={designSystemStyles.sizeRowMedium}
                            gridTemplateColumns={tableGridTemplate}
                            gap={designSystemStyles.gapSm}
                        >
                            <TwoFactorAuthenticationTableText text={row.name} isDimmed={row.isDimmed} />
                            <TwoFactorAuthenticationTableText text={row.identification} isDimmed={row.isDimmed} />
                            <TwoFactorAuthenticationTableText text={row.empresa} isDimmed={row.isDimmed} />
                            <TwoFactorAuthenticationTableText text={row.mobilePhone} isDimmed={row.isDimmed} />
                            <HorizontalContainer
                                width="100%"
                                justifyContent="center"
                                minWidth="0"
                            >
                                <QSwitch
                                    checked={row.is2FAEnabled}
                                    disabled={row.isSaving || viewModel.isSavingMode}
                                    onChange={(event) => {
                                        void viewModel.toggleSubject(row.id, event.target.checked);
                                    }}
                                    inputProps={{
                                        "aria-label": `2FA ${row.name || row.id}`,
                                    }}
                                />
                            </HorizontalContainer>
                            <HorizontalContainer
                                width="100%"
                                justifyContent="center"
                                minWidth="0"
                            >
                                <HollowedButton
                                    text={row.isSendingInvitation ? "Enviando...".localized() : "Invitar".localized()}
                                    disabled={row.isSendingInvitation}
                                    onClick={() => {
                                        void viewModel.sendInstallInvitation(row.id);
                                    }}
                                />
                            </HorizontalContainer>
                        </TableRowContainer>
                    ))}
                </VerticalContainer>
            </VerticalContainer>
        );
    };

    const renderUsersTab = () => (
        <VerticalContainer
            width="100%"
            height="100%"
            gap={designSystemStyles.gapMd}
            alignment="stretch"
            minHeight="0"
        >
            <VerticalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                margin="0"
            >
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    justifyContent="space-between"
                >
                    <HorizontalContainer width="420px">
                        <ComboBox
                            options={modeComboOptions}
                            value={selectedModeOption}
                            placeholder={"Seleccionar modo".localized()}
                            disabled={viewModel.isSavingMode}
                            onChange={(option) => {
                                void viewModel.updateMode(option.id as SecondFactorAuthenticationPolicyMode);
                            }}
                            iconType="settings"
                        />
                    </HorizontalContainer>
                    <HorizontalContainer
                        justifyContent="flex-start"
                        alignment="center"
                        height="100%"
                    >
                        <SmallText
                            text={(selectedModeDescription ?? "").localized()}
                            color={designSystemStyles.colorTextSecondary}
                        />
                    </HorizontalContainer>
                </HorizontalContainer>
            </VerticalContainer>

            <HorizontalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                alignment="center"
                justifyContent="space-between"
            >
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    justifyContent="flex-start"
                >
                    <SearchBar
                        placeholder={"Buscar por nombre, identificacion, empresa o celular".localized()}
                        value={viewModel.searchQuery}
                        onChange={viewModel.setSearchQuery}
                        disabled={viewModel.isLoading}
                        width="100%"
                    />
                    <RefreshButton
                        onClick={viewModel.refresh}
                        loading={viewModel.isRefreshing}
                        disabled={viewModel.isLoading}
                    />
                </HorizontalContainer>
                <FilterPillGroup
                    ariaLabel={"Filtrar por estado de 2FA".localized()}
                    wrap={false}
                    selectedId={viewModel.statusFilter}
                    onSelect={(id) => viewModel.setStatusFilter(id as TwoFactorStatusFilter)}
                    options={[
                        { id: "all", label: "Todos".localized(), count: viewModel.totalCount },
                        { id: "enabled", label: "Con 2FA".localized(), count: viewModel.enabledCount, dotColor: designSystemStyles.colorSuccess },
                        { id: "disabled", label: "Sin 2FA".localized(), count: viewModel.disabledCount, dotColor: designSystemStyles.colorTextMuted },
                    ] as FilterPillOption[]}
                />
            </HorizontalContainer>

            <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
                {renderUsersBody()}
            </div>
        </VerticalContainer>
    );

    const renderDevicesBody = () => {
        if (devicesViewModel.isLoading) {
            return (
                <QontyLoading
                    text={"Cargando dispositivos 2FA...".localized()}
                    withQonty={true}
                    darkMode={false}
                />
            );
        }

        if (devicesViewModel.errorMessage) {
            return (
                <VerticalContainer
                    width="100%"
                    height="100%"
                    alignment="center"
                    justifyContent="center"
                    gap={designSystemStyles.gapMd}
                >
                    <QontyEmptyState
                        text={devicesViewModel.errorMessage}
                        darkMode={false}
                    />
                    <RefreshButton
                        onClick={devicesViewModel.refresh}
                        loading={devicesViewModel.isRefreshing}
                    />
                </VerticalContainer>
            );
        }

        if (devicesViewModel.totalCount === 0) {
            return (
                <QontyEmptyState
                    text={"No hay dispositivos instalados para 2FA.".localized()}
                    darkMode={false}
                />
            );
        }

        if (devicesViewModel.visibleRows.length === 0) {
            return (
                <QontyEmptyState
                    text={"No hay dispositivos que coincidan con la busqueda.".localized()}
                    darkMode={false}
                />
            );
        }

        return (
            <VerticalContainer
                width="100%"
                height="100%"
                gap={designSystemStyles.gapSm}
                minHeight="0"
            >
                <TableHeaderContainer
                    height={designSystemStyles.sizeRowMedium}
                    gridTemplateColumns={devicesTableGridTemplate}
                    gap={designSystemStyles.gapSm}
                >
                    <TableHeaderCell text="Colaborador" truncate={true} />
                    <TableHeaderCell text="Dispositivo" truncate={true} />
                    <TableHeaderCell text="Plataforma" truncate={true} />
                    <TableHeaderCell text="Version" truncate={true} />
                    <TableHeaderCell text="Estado" truncate={true} />
                    <TableHeaderCell text="Ultima actualizacion" truncate={true} />
                    <TableHeaderCell text="Acciones" justifyContent="center" />
                </TableHeaderContainer>
                <VerticalContainer
                    width="100%"
                    height="100%"
                    minHeight="0"
                    scrollable={true}
                    gap={designSystemStyles.gapSm}
                >
                    {devicesViewModel.visibleRows.map((row) => {
                        const isExpanded = expandedDeviceInstallationID === row.deviceInstallationID;

                        return (
                            <React.Fragment key={row.deviceInstallationID}>
                                <TableRowContainer
                                    minHeight={designSystemStyles.sizeRowMedium}
                                    gridTemplateColumns={devicesTableGridTemplate}
                                    gap={designSystemStyles.gapSm}
                                    backgroundColor={isExpanded ? designSystemStyles.colorBackgroundGray3 : undefined}
                                >
                                    <TwoFactorAuthenticationTableText text={row.collaboratorName} isDimmed={!row.isActive} />
                                    <TwoFactorAuthenticationTableText text={row.deviceLabel} isDimmed={!row.isActive} />
                                    <TwoFactorAuthenticationTableText text={row.platformLabel} isDimmed={!row.isActive} />
                                    <TwoFactorAuthenticationTableText text={row.appVersionLabel} isDimmed={!row.isActive} />
                                    <TwoFactorAuthenticationTableText text={row.statusLabel} isDimmed={!row.isActive} />
                                    <TwoFactorAuthenticationTableText text={row.updatedAtLabel} isDimmed={!row.isActive} />
                                    <HorizontalContainer
                                        width="100%"
                                        justifyContent="center"
                                        alignment="center"
                                        gap={designSystemStyles.gapSm}
                                        minWidth="0"
                                    >
                                        <IconButton
                                            iconType="eye"
                                            tooltip={(isExpanded ? "Ocultar detalle" : "Ver detalle").localized()}
                                            onClick={() => setExpandedDeviceInstallationID(
                                                isExpanded ? undefined : row.deviceInstallationID,
                                            )}
                                        />
                                        <IconButton
                                            iconType="delete"
                                            tooltip={"Desactivar dispositivo".localized()}
                                            disabled={!row.isActive || row.isSaving}
                                            loading={row.isSaving}
                                            onClick={() => setDeviceToDeactivate(row)}
                                        />
                                    </HorizontalContainer>
                                </TableRowContainer>
                                {isExpanded && (
                                    <div
                                        style={{
                                            backgroundColor: designSystemStyles.colorBackgroundGray3,
                                            borderLeft: `3px solid ${designSystemStyles.colorMainAction}`,
                                            borderRadius: designSystemStyles.cornerRadiusComponent,
                                            padding: designSystemStyles.paddingMd,
                                        }}
                                    >
                                        <ColumnsContainer
                                            minColumnWidth="200px"
                                            gap={designSystemStyles.gapMd}
                                        >
                                            <DetailField label={"Colaborador".localized()} value={row.collaboratorName} />
                                            <DetailField label={"Dispositivo".localized()} value={row.deviceLabel} />
                                            <DetailField label={"Plataforma".localized()} value={row.platformLabel} />
                                            {row.deviceModel && (
                                                <DetailField label={"Modelo".localized()} value={row.deviceModel} />
                                            )}
                                            {row.osVersion && (
                                                <DetailField label={"Sistema operativo".localized()} value={row.osVersion} />
                                            )}
                                            <DetailField label={"Version".localized()} value={row.appVersionLabel} />
                                            <DetailField label={"Estado".localized()} value={row.statusLabel} />
                                            <DetailField label={"Registrado".localized()} value={row.createdAtLabel} />
                                            <DetailField label={"Ultima actualizacion".localized()} value={row.updatedAtLabel} />
                                            {!row.isActive && (
                                                <>
                                                    <DetailField label={"Desactivado".localized()} value={row.deactivatedAtLabel} />
                                                    <DetailField label={"Desactivado por".localized()} value={row.deactivatedByLabel} />
                                                </>
                                            )}
                                        </ColumnsContainer>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </VerticalContainer>
            </VerticalContainer>
        );
    };

    const renderDevicesTab = () => (
        <VerticalContainer
            width="100%"
            height="100%"
            gap={designSystemStyles.gapMd}
            alignment="stretch"
            minHeight="0"
        >
            <HorizontalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                alignment="center"
                justifyContent="space-between"
            >
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    justifyContent="flex-start"
                >
                    <SearchBar
                        placeholder={"Buscar por colaborador, dispositivo, plataforma, version o estado".localized()}
                        value={devicesViewModel.searchQuery}
                        onChange={devicesViewModel.setSearchQuery}
                        disabled={devicesViewModel.isLoading}
                        width="100%"
                    />
                    <RefreshButton
                        onClick={devicesViewModel.refresh}
                        loading={devicesViewModel.isRefreshing}
                        disabled={devicesViewModel.isLoading}
                    />
                </HorizontalContainer>
                <FilterPillGroup
                    ariaLabel={"Filtrar por estado del dispositivo".localized()}
                    wrap={false}
                    selectedId={devicesViewModel.statusFilter}
                    onSelect={(id) => devicesViewModel.setStatusFilter(id as DeviceStatusFilter)}
                    options={[
                        { id: "all", label: "Todos".localized(), count: devicesViewModel.totalCount },
                        { id: "active", label: "Activos".localized(), count: devicesViewModel.activeCount, dotColor: designSystemStyles.colorSuccess },
                        { id: "inactive", label: "Inactivos".localized(), count: devicesViewModel.inactiveCount, dotColor: designSystemStyles.colorTextMuted },
                    ] as FilterPillOption[]}
                />
            </HorizontalContainer>

            <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
                {renderDevicesBody()}
            </div>
        </VerticalContainer>
    );

    const flaps: Flap[] = [
        {
            id: "users",
            label: "Usuarios".localized(),
            content: renderUsersTab(),
        },
        {
            id: "devices",
            label: "Dispositivos".localized(),
            content: renderDevicesTab(),
        },
    ];

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title="2FA"
                width="1280px"
                height="900px"
                maxWidth="92vw"
                maxHeight="85vh"
            >
                <VerticalContainer
                    width="100%"
                    height="100%"
                    gap={designSystemStyles.gapNone}
                    alignment="stretch"
                    minHeight="0"
                >
                    <TabScreenSleek
                        title="2FA"
                        flaps={flaps}
                        clickedScreen={selectedTab}
                        setClickedScreen={setSelectedTab}
                    />
                    <HorizontalContainer
                        width="100%"
                        justifyContent="space-between"
                        alignment="center"
                        gap={designSystemStyles.gapMd}
                        paddingTop={designSystemStyles.paddingMd}
                    >
                        <SmallText
                            text={viewModel.lastUpdatedLabel ?? "Sin actualizaciones registradas".localized()}
                            color={designSystemStyles.colorTextSecondary}
                        />
                        <HollowedButton
                            text={"Cerrar".localized()}
                            onClick={onClose}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
            <Modal
                isOpen={!!deviceToDeactivate}
                onClose={() => setDeviceToDeactivate(undefined)}
                title={"Desactivar dispositivo".localized()}
                width="520px"
                height="auto"
                maxWidth="92vw"
                maxHeight="80vh"
            >
                <VerticalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                >
                    <Text
                        text={`¿Desactivar el dispositivo ${deviceToDeactivate?.deviceLabel ?? ""}?`.localized()}
                    />
                    <SmallText
                        text={"El colaborador tendra que volver a instalar o vincular un dispositivo para completar 2FA.".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                    <HorizontalContainer
                        width="100%"
                        justifyContent="flex-end"
                        gap={designSystemStyles.gapSm}
                    >
                        <HollowedButton
                            text={"Cancelar".localized()}
                            onClick={() => setDeviceToDeactivate(undefined)}
                            disabled={isDeactivationConfirmationSaving}
                        />
                        <NormalButton
                            text={"Desactivar".localized()}
                            onClick={() => void handleConfirmDeactivateDevice()}
                            loading={isDeactivationConfirmationSaving}
                        />
                    </HorizontalContainer>
                </VerticalContainer>
            </Modal>
        </>
    );
};

export default TwoFactorAuthenticationModalScreen;
